import L from 'leaflet';
import { SecurityInventoryCategory, SecurityInventoryItem } from '@x-guard/xgac-types/xgac';
import {
  Identifier, useTranslate, Datagrid, Confirm,
} from 'react-admin';
import {
  useCallback,
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useGetList, useRefresh, useNotify } from 'ra-core';
import { useSearchParams } from 'react-router-dom';
import { Box, TableHead } from '@mui/material';
import {
  hasItemPosition, itemToLatLng, securityItemsResource, appUsersResource,
  InventoryItemFilter,
} from '../util';
import { ItemCategorySettingsButton } from './ItemCategorySettingsButton';
import { BulkActionsSection } from './BulkActionsSection';
import { CreateItemSection } from './CreateItemSection';
import { FilterButton } from './FilterButton';
import { SearchInput } from '../../../components/SearchInput';
import { SelectAllButton } from './SelectAllButton';
import { DataGridListItem } from './DataGridListItem';
import { xgacDataProvider } from '../../../../../dataProviders/xgacDataProvider';
import { EditItemModal } from './EditItemModal';

export const InventoryItemsSidebar = (props: {
  itemsData: SecurityInventoryItem[];
  itemsLoading: boolean;
  categoriesData: SecurityInventoryCategory[];
  map: L.Map | null;
  focusedItemId: Identifier | null;
  setFocusedItemId: React.Dispatch<React.SetStateAction<Identifier | null>>;
  setFilteredItems: React.Dispatch<React.SetStateAction<SecurityInventoryItem[]>>;
}) => {

  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();

  const [mouseHoverId, setMouseHoverId] = useState<Identifier | null>(null);
  const [selectedIds, setSelectedIds] = useState<Identifier[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<InventoryItemFilter>({ categories: [], users: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [editItemModalOpen, setEditItemModalOpen] = useState(false);
  const [editItemRecord, setEditItemRecord] = useState<SecurityInventoryItem | null>(null);
  const idsBeforeCreate = useRef<Set<Identifier>>(new Set());
  const [openEditAfterCreate, setOpenEditAfterCreate] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const showBulkActions = selectedIds.length > 0;
  const setFilteredItems = props.setFilteredItems;
  const map = props.map;
  const setFocusedItemId = props.setFocusedItemId;
  const editItemId = searchParams.get('editItem');

  // For all of the fetched resources, all of the data is fetched at once without pagination

  const { data: usersData } = useGetList(appUsersResource, {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  });

  // Both search and filter are applied together
  // Note: the categoriesData is used to check if the selected user is in the category's peopleToNotify array
  const filteredItems = useMemo(() => {

    const list = props.itemsData ?? [];
    const q = searchTerm.trim().toLowerCase();

    // Map is used for faster lookup of peopleToNotify by categoryId
    const peopleToNotifyByCategoryId = new Map<string, Set<string>>();
    for (const category of props.categoriesData ?? []) {

      peopleToNotifyByCategoryId.set(
        category._id,
        new Set(
          (category.notifiedPeople?.peopleToNotify ?? []).map((entry) => (typeof entry === 'string' ? entry : entry._id)),
        ),
      );

    }
    const { categories: selectedCategories, users: selectedUsers } = selectedFilters;

    return list.filter((item) => {

      const categoryId = item.category._id;

      if (selectedCategories.length > 0 && !selectedCategories.includes(categoryId)) return false;
      if (selectedUsers.length > 0) {

        const peopleToNotifySet = peopleToNotifyByCategoryId.get(categoryId);
        const matchesAnyUser = selectedUsers.some((userId) => peopleToNotifySet?.has(userId));
        if (!matchesAnyUser) return false;

      }

      if (q && !(item.name ?? '').toLowerCase().includes(q)) return false;
      return true;

    });

  }, [props.itemsData, selectedFilters, searchTerm, props.categoriesData]);

  const focusItemFromList = useCallback((itemId: Identifier, item: SecurityInventoryItem) => {

    setFocusedItemId(itemId);
    if (hasItemPosition(item) && map) {

      const latLng = L.latLng(itemToLatLng(item));
      if (!map.getBounds().contains(latLng)) {

        map.flyTo(latLng);

      }

    }

  }, [map, setFocusedItemId]);

  const openEditItemModal = useCallback((item: SecurityInventoryItem) => {

    setEditItemRecord(item);
    setEditItemModalOpen(true);
    focusItemFromList(item._id, item);

  }, [focusItemFromList]);

  const closeEditItemModal = useCallback(() => {

    setEditItemModalOpen(false);
    setEditItemRecord(null);

  }, []);

  const handleBeforeCreate = () => {

    idsBeforeCreate.current = new Set(props.itemsData.map((item) => item._id));

  };

  const handleAfterCreate = () => {

    setOpenEditAfterCreate(true);

  };

  const handleDeleteItems = async () => {

    if (selectedIds.length === 0) return;
    try {

      await xgacDataProvider.deleteMany(securityItemsResource, { ids: selectedIds });
      notify('ra.notification.deleted', { type: 'info', messageArgs: { smart_count: selectedIds.length } });
      refresh();
      if (props.focusedItemId && selectedIds.includes(props.focusedItemId)) {

        setFocusedItemId(null);

      }
      setSelectedIds([]);
      setDeleteDialogOpen(false);

    } catch {

      notify('ra.notification.http_error', { type: 'warning' });
      setDeleteDialogOpen(false);

    }

  };

  useEffect(() => {

    if (!openEditAfterCreate) return;

    const newItems = props.itemsData.filter((item) => !idsBeforeCreate.current.has(item._id));
    if (newItems.length === 0) return;

    openEditItemModal(newItems[0]);
    setOpenEditAfterCreate(false);

  }, [openEditAfterCreate, props.itemsData, openEditItemModal, setOpenEditAfterCreate]);

  useEffect(() => {

    if (!editItemId || props.itemsLoading) return;

    const item = props.itemsData.find((record) => record._id === editItemId);
    if (!item) {

      setEditItemRecord(null);
      setEditItemModalOpen(false);
      return;

    }

    openEditItemModal(item);
    setSearchParams((params) => {

      const updatedSearchParams = new URLSearchParams(params);
      updatedSearchParams.delete('editItem');
      return updatedSearchParams;

    }, { replace: true });

  }, [editItemId, props.itemsData, props.itemsLoading, openEditItemModal, setSearchParams]);

  useEffect(() => {

    setFilteredItems(filteredItems);

  }, [filteredItems, setFilteredItems]);

  useEffect(() => {

    if (!props.focusedItemId) return;
    requestAnimationFrame(() => {

      document.getElementById(`inventory-item-${props.focusedItemId}`)?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });

    });

  }, [props.focusedItemId]);

  return (
    <>
      <div className="button-sidebar-div">
        <div className="button-sidebar-add">
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
            <ItemCategorySettingsButton />
          </Box>
          <h4>
            <span>{showBulkActions ? translate('resources.buttons.text.actions_with_selected') : translate('menu.items.security_inventory')}</span>
          </h4>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {showBulkActions && <BulkActionsSection onDelete={() => setDeleteDialogOpen(true)}/>}
            {!showBulkActions && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <CreateItemSection
                categories={props.categoriesData || []}
                map={map}
                onBeforeCreate={handleBeforeCreate}
                onAfterCreate={handleAfterCreate}
              />
              <FilterButton
                categories={props.categoriesData || []}
                filters={selectedFilters}
                setFilters={setSelectedFilters}
                users={usersData || []}
              >
                <SearchInput searchTerm={searchTerm} setSearchTerm={setSearchTerm} placeholder={translate('resources.security-inventory-items.text.search')}/>
              </FilterButton>
            </Box>}
            {showBulkActions && <SelectAllButton selectedIds={selectedIds} setSelectedIds={setSelectedIds} items={props.itemsData} />}
          </Box>
        </div>
        <div className="button-sidebar-grid">
          <Datagrid
            resource={securityItemsResource}
            data={filteredItems}
            isLoading={props.itemsLoading}
            total={filteredItems.length}
            empty={
              <Box sx={{
                px: 2, py: 3, textAlign: 'center', color: 'text.secondary', fontSize: 14,
              }}>
                {translate('resources.security-inventory-items.text.no_results')}
              </Box>
            }
            bulkActionButtons={false}
            rowClick={false}
            header={<TableHead />}
            rowStyle={(record: SecurityInventoryItem, index: number) => {

              if (selectedIds.includes(record._id)) {

                return {
                  backgroundColor: '#9FD2B6',
                };

              }
              if (props.focusedItemId === record._id) {

                return {
                  backgroundColor: index % 2 === 0 ? '#E8F4EC' : '#D8EDE4',
                  outline: '2px solid #2E7D57',
                  outlineOffset: -2,
                };

              }
              return {
                backgroundColor: index % 2 === 0 ? '#F0F9F5' : '#E2F1EA',
              };

            }}
            sx={{
              '& .RaDatagrid-expandIconCell': {
                display: 'none',
              },
            }}
          >
            <DataGridListItem
              categories={props.categoriesData || []}
              mouseHoverId={mouseHoverId}
              setMouseHoverId={setMouseHoverId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              onFocus={focusItemFromList}
              onEdit={openEditItemModal}
            />
          </Datagrid>
        </div>
      </div>
      <Confirm
        isOpen={deleteDialogOpen}
        onConfirm={handleDeleteItems}
        onClose={() => setDeleteDialogOpen(false)}
        title={translate('resources.security-inventory-items.text.delete_dialog_title')}
        content={translate('resources.security-inventory-items.text.delete_content')}
      />
      <EditItemModal
        record={editItemRecord}
        categories={props.categoriesData || []}
        open={editItemModalOpen}
        onClose={closeEditItemModal}
      />
    </>
  );

};
